import React from 'react';

export default function FeaturesBento() {
  return (
    <section id="features" className="py-24 bg-slate-950/90 relative overflow-hidden border-t border-b border-white/5">
      {/* Background Glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3">
            BUILT FOR SCHOOL EXCELLENCE
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl font-extrabold text-white tracking-tight mb-4">
            Everything you need to run a smarter school.
          </h2>
          <p className="text-slate-400 text-base sm:text-lg">
            Powerful modular features engineered specifically for K-12 and secondary school administrative requirements.
          </p>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Card 1: Smart Attendance (Large 2-col) */}
          <div className="md:col-span-2 bg-gradient-to-br from-slate-900/90 to-slate-900/40 border border-emerald-500/30 rounded-3xl p-8 flex flex-col justify-between group hover:border-emerald-500/60 transition-all duration-300 shadow-xl">
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full">
                  Real-time Roll Call
                </span>
                <span className="text-2xl">✅</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-emerald-300 transition-colors">
                Smart Attendance Tracking
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Teachers log daily attendance in seconds. Administrators and parents track attendance percentages, absent patterns, and automated monthly compliance metrics.
              </p>
            </div>

            {/* Visual Attendance Preview */}
            <div className="bg-slate-950/80 border border-white/10 rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between text-xs text-slate-300">
                <span className="font-semibold">Grade 10-A Attendance</span>
                <span className="text-emerald-400 font-bold">96.8% Present</span>
              </div>
              <div className="w-full bg-slate-800 h-2.5 rounded-full overflow-hidden flex">
                <div className="bg-emerald-500 h-full w-[94%]" />
                <div className="bg-amber-500 h-full w-[4%]" />
                <div className="bg-red-500 h-full w-[2%]" />
              </div>
              <div className="flex justify-between text-[10px] text-slate-400">
                <span>Present: 28</span>
                <span>Late: 1</span>
                <span>Absent: 1</span>
              </div>
            </div>
          </div>

          {/* Card 2: Quizzes & Assessments (Large 2-col) */}
          <div className="md:col-span-1 lg:col-span-2 bg-gradient-to-br from-slate-900/90 to-slate-900/40 border border-purple-500/30 rounded-3xl p-8 flex flex-col justify-between group hover:border-purple-500/60 transition-all duration-300 shadow-xl">
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-purple-400 bg-purple-500/10 border border-purple-500/20 px-3 py-1 rounded-full">
                  Automated Grading
                </span>
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors">
                Quizzes & Assessments
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Teachers build interactive multiple-choice quizzes with custom timers. Students take assessments directly in their workspace with instant score calculation.
              </p>
            </div>

            {/* Visual Quiz Card Preview */}
            <div className="bg-slate-950/80 border border-white/10 rounded-2xl p-4 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-white">Biology Midterm MCQ</span>
                <span className="text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded font-mono">15m Timer</span>
              </div>
              <div className="text-xs text-slate-400">Auto-graded • 20 Questions</div>
              <div className="w-full bg-purple-500/20 text-purple-300 text-xs font-bold py-1.5 rounded-lg text-center border border-purple-500/30">
                Avg Score: 18.5 / 20 (92.5%)
              </div>
            </div>
          </div>

          {/* Card 3: Assignments */}
          <div className="bg-slate-900/60 border border-white/10 rounded-3xl p-6 hover:border-amber-500/40 transition-all duration-300 group">
            <div className="text-2xl mb-4 p-3 bg-amber-500/10 text-amber-400 rounded-2xl w-fit border border-amber-500/20">
              📝
            </div>
            <h4 className="text-lg font-bold text-white mb-2 group-hover:text-amber-300">
              Assignments & Submissions
            </h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Upload homework assignments, set deadlines, collect student file submissions, and return detailed teacher feedback.
            </p>
          </div>

          {/* Card 4: Timetables */}
          <div className="bg-slate-900/60 border border-white/10 rounded-3xl p-6 hover:border-blue-500/40 transition-all duration-300 group">
            <div className="text-2xl mb-4 p-3 bg-blue-500/10 text-blue-400 rounded-2xl w-fit border border-blue-500/20">
              📅
            </div>
            <h4 className="text-lg font-bold text-white mb-2 group-hover:text-blue-300">
              Interactive Timetables
            </h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Organize weekly subject schedules, period timings, and classroom locations for teachers and students.
            </p>
          </div>

          {/* Card 5: Analytics & KPIs */}
          <div className="bg-slate-900/60 border border-white/10 rounded-3xl p-6 hover:border-teal-500/40 transition-all duration-300 group">
            <div className="text-2xl mb-4 p-3 bg-teal-500/10 text-teal-400 rounded-2xl w-fit border border-teal-500/20">
              📈
            </div>
            <h4 className="text-lg font-bold text-white mb-2 group-hover:text-teal-300">
              Analytics & KPIs
            </h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Monitor school-wide enrollment metrics, attendance averages, subject pass rates, and class standings.
            </p>
          </div>

          {/* Card 6: Direct Messaging */}
          <div className="bg-slate-900/60 border border-white/10 rounded-3xl p-6 hover:border-sky-500/40 transition-all duration-300 group">
            <div className="text-2xl mb-4 p-3 bg-sky-500/10 text-sky-400 rounded-2xl w-fit border border-sky-500/20">
              💬
            </div>
            <h4 className="text-lg font-bold text-white mb-2 group-hover:text-sky-300">
              Direct Messaging
            </h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Secure in-app communication between admins, teachers, parents, and students with unread notification badges.
            </p>
          </div>

          {/* Card 7: Unified Grade Book (Large 2-col bottom) */}
          <div className="md:col-span-2 bg-gradient-to-br from-slate-900/90 to-slate-900/40 border border-blue-500/30 rounded-3xl p-8 flex flex-col justify-between group hover:border-blue-500/60 transition-all duration-300 shadow-xl">
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-full">
                  Unified Grade Book
                </span>
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors">
                Comprehensive Grade Book
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Combines daily attendance rates, quiz averages, and coursework grades into a clear, weighted academic performance matrix.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 bg-slate-950/80 border border-white/10 p-3 rounded-2xl text-center text-xs">
              <div className="bg-white/5 p-2 rounded-xl">
                <div className="text-slate-400 text-[10px]">Attendance %</div>
                <div className="text-emerald-400 font-bold">95% (30 pts)</div>
              </div>
              <div className="bg-white/5 p-2 rounded-xl">
                <div className="text-slate-400 text-[10px]">Quiz Avg</div>
                <div className="text-purple-400 font-bold">88% (40 pts)</div>
              </div>
              <div className="bg-white/5 p-2 rounded-xl">
                <div className="text-slate-400 text-[10px]">Final Standing</div>
                <div className="text-blue-400 font-bold">90.1% (A)</div>
              </div>
            </div>
          </div>

          {/* Card 8: PDF Reports (Large 2-col bottom) */}
          <div className="md:col-span-2 bg-gradient-to-br from-slate-900/90 to-slate-900/40 border border-violet-500/30 rounded-3xl p-8 flex flex-col justify-between group hover:border-violet-500/60 transition-all duration-300 shadow-xl">
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-violet-400 bg-violet-500/10 border border-violet-500/20 px-3 py-1 rounded-full">
                  Official Documents
                </span>
                <span className="text-2xl">📄</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-violet-300 transition-colors">
                Instant PDF Report Cards
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Generate professional PDF academic report cards and attendance logs with one click for printing or digital distribution.
              </p>
            </div>

            <div className="flex items-center justify-between bg-slate-950/80 border border-white/10 p-3 rounded-2xl text-xs">
              <div className="flex items-center gap-3">
                <span className="text-xl">📜</span>
                <div>
                  <div className="font-bold text-white">Student_Report_Term1.pdf</div>
                  <div className="text-[10px] text-slate-400">Official Seal Verified</div>
                </div>
              </div>
              <span className="bg-violet-600 text-white font-bold px-3 py-1.5 rounded-lg shadow-sm">
                Download PDF
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
